import dayjs from "dayjs";
import type { AskHelenaConversation } from "shared";
import type { ChatHistoryEntry, ChatHistoryGroup } from "../types";

/** Bucket labels, in the order the sidebar shows them. */
const TODAY = "Today";
const YESTERDAY = "Yesterday";
const PREVIOUS_WEEK = "Previous 7 days";
const OLDER = "Older";

const DAYS_IN_PREVIOUS_WEEK = 7;

export const ASK_HELENA_BUCKET_LABELS = {
  today: TODAY,
  yesterday: YESTERDAY,
  previousWeek: PREVIOUS_WEEK,
  older: OLDER,
} as const;

/**
 * The service names a thread from its first exchange, but only best-effort:
 * a refused first turn, an exchange too thin to summarise, or a model blip all
 * leave `title` null — permanently. So this fallback is not transitional and
 * must not be removed. A bare "Untitled chat" would make ten rows
 * indistinguishable, so it carries the timestamp: the time for today's threads,
 * the date for the rest.
 */
export const askHelenaConversationTitle = (
  conversation: AskHelenaConversation,
): string => {
  const title = conversation.title?.trim();
  if (title) return title;

  const updatedAt = dayjs(conversation.updatedAt);
  const isToday = updatedAt.isSame(dayjs(), "day");
  return `Untitled · ${updatedAt.format(isToday ? "h:mm A" : "MMM D")}`;
};

const toEntry = (conversation: AskHelenaConversation): ChatHistoryEntry => ({
  id: conversation.id,
  title: askHelenaConversationTitle(conversation),
  rawTitle: conversation.title,
  isPinned: conversation.pinnedAt !== null,
});

const bucketLabel = (updatedAt: string): string => {
  // Clock skew can put a just-created thread slightly ahead of now, so a
  // negative difference belongs in Today rather than in a future bucket.
  const daysAgo = dayjs()
    .startOf("day")
    .diff(dayjs(updatedAt).startOf("day"), "day");
  if (daysAgo <= 0) return TODAY;
  if (daysAgo === 1) return YESTERDAY;
  if (daysAgo < DAYS_IN_PREVIOUS_WEEK) return PREVIOUS_WEEK;
  return OLDER;
};

/**
 * Buckets a newest-first list by day. Insertion order into the Map is the
 * display order, which is only correct while the input really is newest-first —
 * so this must never be handed the raw API array, which now leads with the
 * pinned threads. See {@link splitAskHelenaConversations}.
 */
const groupAskHelenaConversations = (
  conversations: AskHelenaConversation[],
): ChatHistoryGroup[] => {
  const buckets = new Map<string, ChatHistoryEntry[]>();

  for (const conversation of conversations) {
    const label = bucketLabel(conversation.updatedAt);
    const entry = toEntry(conversation);

    const existing = buckets.get(label);
    if (existing) {
      existing.push(entry);
    } else {
      buckets.set(label, [entry]);
    }
  }

  return [...buckets].map(([label, entries]) => ({ label, entries }));
};

/** The sidebar's two sections, both in the order they should be rendered. */
export interface AskHelenaConversationSections {
  /** Server order, untouched — most recently pinned first. */
  pinned: ChatHistoryEntry[];
  /** Day buckets over the unpinned threads only. */
  recent: ChatHistoryGroup[];
}

/**
 * Splits one page of the API's list into the sidebar's Pinned and Recent
 * sections. TWO passes, and the order of the two is the whole point.
 *
 * The list now arrives pinned-first (most recently pinned at the top), then
 * everything else newest-first. Bucketing that raw array by day breaks three
 * ways at once: an "Older" bucket rendered above "Today" because the pinned row
 * is met first; a SECOND "Today" bucket when a Today thread follows a pinned one
 * from another week; and every pinned thread listed twice, once in each section.
 *
 * Partitioning on `pinnedAt` first fixes all three, because each side of the
 * split is still in the order the server sorted it: `pinned` is
 * most-recently-pinned first, and `unpinned` is newest-first — exactly what the
 * day bucketer's insertion-order assumption requires.
 *
 * Neither side is ever sorted here. `pinnedAt` decides which section a row is
 * in; it never decides where it sits within one.
 */
export const splitAskHelenaConversations = (
  conversations: AskHelenaConversation[],
): AskHelenaConversationSections => {
  const pinned: AskHelenaConversation[] = [];
  const unpinned: AskHelenaConversation[] = [];

  for (const conversation of conversations) {
    (conversation.pinnedAt !== null ? pinned : unpinned).push(conversation);
  }

  return {
    pinned: pinned.map(toEntry),
    recent: groupAskHelenaConversations(unpinned),
  };
};

/**
 * Merges the listed row with the session's stand-in. The server's own title
 * wins as soon as it has one — the session label is only the first question,
 * standing in until the auto-titler names the thread a second or two after the
 * first turn. Everything else on the row (pin state, id) comes from the server.
 */
const mergeSessionEntry = (
  listed: ChatHistoryEntry,
  sessionChat: ChatHistoryEntry,
): ChatHistoryEntry =>
  listed.rawTitle ? listed : { ...listed, title: sessionChat.title };

/**
 * Places the thread on screen into the sidebar, so a conversation the list has
 * never seen still appears while the user is in it.
 *
 * Three cases, in order:
 *  - Already in Pinned — leave both sections alone. Prepending to Recent would
 *    show the same thread in two sections at once.
 *  - Already in Recent — merge in place. Prepending again would show it twice,
 *    and would drag an older re-opened thread up into Today.
 *  - Not listed at all — prepend to Today. A brand-new thread can only be from
 *    today, and cannot be pinned.
 */
export const withSessionChat = (
  sections: AskHelenaConversationSections,
  sessionChat: ChatHistoryEntry | undefined,
): AskHelenaConversationSections => {
  if (!sessionChat) return sections;

  const { pinned, recent } = sections;

  const pinnedEntry = pinned.find((entry) => entry.id === sessionChat.id);
  if (pinnedEntry) {
    return {
      pinned: pinned.map((entry) =>
        entry.id === sessionChat.id
          ? mergeSessionEntry(entry, sessionChat)
          : entry,
      ),
      recent,
    };
  }

  const isListed = recent.some((group) =>
    group.entries.some((entry) => entry.id === sessionChat.id),
  );
  if (isListed) {
    return {
      pinned,
      recent: recent.map((group) => ({
        label: group.label,
        entries: group.entries.map((entry) =>
          entry.id === sessionChat.id
            ? mergeSessionEntry(entry, sessionChat)
            : entry,
        ),
      })),
    };
  }

  const [first, ...rest] = recent;
  if (first?.label === TODAY) {
    return {
      pinned,
      recent: [
        { label: TODAY, entries: [sessionChat, ...first.entries] },
        ...rest,
      ],
    };
  }

  return {
    pinned,
    recent: [{ label: TODAY, entries: [sessionChat] }, ...recent],
  };
};
